import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/api";

const getEvent = () => {
  try {
    const event = localStorage.getItem("event");
    return event;
  } catch (error) {
    console.error("Token decode error", error);
    return null;
  }
};

const PhotoGallery = ({ photos, title }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  if (!photos || photos.length === 0) return null;

  return (
    <div className="mt-3">
      <p className="text-xs font-medium text-slate-600 mb-2">{title}</p>
      <div className="grid grid-cols-3 gap-2">
        {photos.map((photo, idx) => (
          <div
            key={idx}
            className="relative aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity border border-slate-200"
            onClick={() => setSelectedPhoto(photo)}
          >
            <img
              src={typeof photo === 'string' ? photo : URL.createObjectURL(photo)}
              alt={`${title} ${idx + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>

      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[100] p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-5xl max-h-[90vh]">
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute -top-12 right-0 text-white text-4xl hover:text-gray-300"
            >
              ×
            </button>
            <img
              src={typeof selectedPhoto === 'string' ? selectedPhoto : URL.createObjectURL(selectedPhoto)}
              alt="Full size"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const Clean = () => {
  const eventId = getEvent();

  const [createdTickets, setCreatedTickets] = useState([]);
  const [assignedTickets, setAssignedTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [activeTab, setActiveTab] = useState("created");

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [type, setType] = useState("stand");
  const [stand, setStand] = useState("");
  const [hall, setHall] = useState("");
  const [stands, setStands] = useState([]);
  const [halls, setHalls] = useState([]);
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [slaValue, setSlaValue] = useState(24);
  const [slaUnit, setSlaUnit] = useState("hours");
  const [createPhotos, setCreatePhotos] = useState([]);

  const [message, setMessage] = useState("");
  const [messagePhotos, setMessagePhotos] = useState([]);
  const [closePhotos, setClosePhotos] = useState([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);

  const fetchTickets = async () => {
    try {
      const res = await axiosInstance.get("/ticket");

      const created = Array.isArray(res.data.createdTickets) ? res.data.createdTickets : [];
      const assigned = Array.isArray(res.data.assignedTickets) ? res.data.assignedTickets : [];

      setCreatedTickets(created);
      setAssignedTickets(assigned);
    } catch (error) {
      console.error("Fetch tickets error", error);
      setCreatedTickets([]);
      setAssignedTickets([]);
    }
  };

  const fetchStands = async () => {
    try {
      const res = await axiosInstance.get(`/stand/${eventId}`);
      setStands(res.data.stands);
    } catch (error) {
      console.error("Fetch stands error", error);
    }
  };

  const fetchHalls = async () => {
    try {
      const res = await axiosInstance.get(`stand/halls/${eventId}`);      
      setHalls(res.data.halls);
    } catch (error) {
      console.error("Fetch halls error", error);
    }
  };

  const fetchTicketById = async (id) => {
    try {
      const res = await axiosInstance.get(`/ticket/${id}`);
      console.log("Ticket detail:", res.data);
      const ticketData = res.data.ticket || res.data;
      setSelectedTicket(ticketData);
    } catch (error) {
      console.error("Fetch ticket error", error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  useEffect(() => {
    fetchTickets();
    fetchStands();
    fetchHalls();
  }, []);

  const createTicket = async () => {
    try {
      const location = type === "stand" ? stand : hall;
      
      if (!location || !description || !createPhotos.length) {
        alert("Location, description and photos are required");
        return;
      }

      // Convert to minutes based on selected unit
      const slaMinutes = slaUnit === "hours" ? slaValue * 60 : slaValue;

      const formData = new FormData();
      formData.append("stand", location);
      formData.append("type", type);
      formData.append("description", description);
      formData.append("priorite", priority);
      formData.append("slaMinutes", slaMinutes);
      formData.append("source", "cleaning");
      formData.append("event", eventId);

      createPhotos.forEach((photo) => formData.append("photos", photo));

      await axiosInstance.post("/ticket", formData);

      setType("stand");
      setStand("");
      setHall("");
      setDescription("");
      setPriority("medium");
      setSlaValue(24);
      setSlaUnit("hours");
      setCreatePhotos([]);
      setShowCreateModal(false);
      fetchTickets();
      alert("Ticket created successfully");
    } catch (error) {
      console.error("Create ticket error", error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const sendMessage = async () => {
    try {
      if (!message && !messagePhotos.length) {
        alert("Message or photo required");
        return;
      }

      const formData = new FormData();
      formData.append("message", message);
      messagePhotos.forEach((file) => formData.append("images", file));

      await axiosInstance.post(`/ticket/message/${selectedTicket._id}`, formData);

      setMessage("");
      setMessagePhotos([]);
      fetchTicketById(selectedTicket._id);
      alert("Message sent");
    } catch (error) {
      console.error("Send message error", error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const closeTicket = async () => {
    if (closePhotos.length === 0) {
      alert("Closing photo required");
      return;
    }

    const formData = new FormData();
    closePhotos.forEach(file => formData.append("photos", file));

    await axiosInstance.put(`/ticket/close/${selectedTicket._id}`, formData);

    setClosePhotos([]);
    fetchTicketById(selectedTicket._id);
    fetchTickets();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "in_progress": return "bg-blue-100 text-blue-800 border-blue-300";
      case "resolved": return "bg-green-100 text-green-800 border-green-300";
      case "rejected": return "bg-red-100 text-red-800 border-red-300";
      default: return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  const getPriorityColor = (priorite) => {
    switch (priorite) {
      case "critical": return "bg-red-500 text-white";
      case "high": return "bg-orange-500 text-white";
      case "medium": return "bg-yellow-500 text-white";
      case "low": return "bg-green-500 text-white";
      default: return "bg-gray-500 text-white";
    }
  };

  const formatSLA = (minutes) => {
    if (!minutes) return "N/A";
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${minutes}m`;
  };

  const currentTickets = activeTab === "created" ? createdTickets : assignedTickets;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-slate-800">Cleaning Ticket Management</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg"
          >
            + Create Ticket
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setActiveTab("created")}
                className={`flex-1 py-2 px-4 rounded-lg font-semibold transition ${activeTab === "created"
                  ? "bg-blue-600 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
              >
                Created ({createdTickets.length})
              </button>
              <button
                onClick={() => setActiveTab("assigned")}
                className={`flex-1 py-2 px-4 rounded-lg font-semibold transition ${activeTab === "assigned"
                  ? "bg-cyan-600 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
              >
                Assigned ({assignedTickets.length})
              </button>
            </div>

            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {!Array.isArray(currentTickets) || currentTickets.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No tickets</p>
              ) : (
                currentTickets.map((ticket) => (
                  <div
                    key={ticket._id}
                    onClick={() => fetchTicketById(ticket._id)}
                    className={`border-2 p-4 rounded-xl cursor-pointer transition-all hover:shadow-lg ${selectedTicket?._id === ticket._id
                      ? "border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100 shadow-md"
                      : "border-slate-200 hover:border-blue-300 bg-white"
                      }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <p className="font-bold text-slate-800 text-lg mb-1">{ticket.stand}</p>
                        <p className="text-sm text-slate-600">
                          {ticket.type && (
                            <span className="inline-flex items-center gap-1 bg-slate-100 px-2 py-1 rounded-full text-xs font-medium">
                              {ticket.type === 'stand' ? '🏢 Stand' : '🏛️ Hall'}
                            </span>
                          )}
                          {ticket.slaMinutes && (
                            <span className="inline-flex items-center gap-1 bg-blue-100 px-2 py-1 rounded-full text-xs font-medium ml-2">
                              ⏱️ SLA: {formatSLA(ticket.slaMinutes)}
                            </span>
                          )}
                        </p>
                      </div>
                      <span className={`text-xs px-3 py-1 rounded-full font-bold shadow-sm ${getPriorityColor(ticket.priorite)}`}>
                        {ticket.priorite}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 mb-3 line-clamp-2">{ticket.description}</p>
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-xs px-3 py-1.5 rounded-full font-semibold border ${getStatusColor(ticket.status)}`}>
                        {ticket.status.replace("_", " ")}
                      </span>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        {ticket.photos && ticket.photos.length > 0 && (
                          <span className="flex items-center gap-1 bg-slate-100 px-2 py-1 rounded-full">
                            📷 {ticket.photos.length}
                          </span>
                        )}
                        <span className="bg-slate-100 px-2 py-1 rounded-full">
                          {new Date(ticket.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
            {!selectedTicket ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-400">
                <span className="text-6xl mb-4">🧹</span>
                <p className="text-lg">Select a ticket to view details</p>
              </div>
            ) : (
              <div className="flex flex-col h-full overflow-y-auto">
                <div className="flex items-start justify-between mb-6 sticky top-0 bg-white pb-3 border-b">
                  <div>
                    <h2 className="font-bold text-2xl text-slate-800 mb-1">{selectedTicket.stand}</h2>
                    <p className="text-sm text-slate-500">
                      Created {new Date(selectedTicket.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <span className={`text-xs px-4 py-2 rounded-full font-bold shadow-sm ${getPriorityColor(selectedTicket.priorite)}`}>
                    {selectedTicket.priorite}
                  </span>
                </div>

                <div className="space-y-4 mb-4">
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-4 rounded-xl border border-slate-200">
                    <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Status</p>
                    <span className={`inline-block text-sm px-4 py-2 rounded-full font-semibold border shadow-sm ${getStatusColor(selectedTicket.status)}`}>
                      {selectedTicket.status.replace("_", " ")}
                    </span>
                  </div>

                  {selectedTicket.type && (
                    <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-4 rounded-xl border border-slate-200">
                      <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Type</p>
                      <span className="inline-flex items-center gap-2 text-sm font-medium text-slate-800">
                        {selectedTicket.type === 'stand' ? '🏢 Stand' : '🏛️ Hall'}
                      </span>
                    </div>
                  )}

                  {selectedTicket.slaMinutes && (
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
                      <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">SLA</p>
                      <span className="inline-flex items-center gap-2 text-sm font-medium text-slate-800">
                        ⏱️ {formatSLA(selectedTicket.slaMinutes)}
                      </span>
                      {selectedTicket.slaDeadline && (
                        <p className="text-xs text-slate-600 mt-2">
                          Deadline: {new Date(selectedTicket.slaDeadline).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-4 rounded-xl border border-slate-200">
                    <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Description</p>
                    <p className="text-sm text-slate-800 leading-relaxed">{selectedTicket.description}</p>
                  </div>

                  {selectedTicket.photos && selectedTicket.photos.length > 0 && (
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
                      <PhotoGallery photos={selectedTicket.photos} title="Ticket Photos" />
                    </div>
                  )}

                  {selectedTicket.closingPhotos && selectedTicket.closingPhotos.length > 0 && (
                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
                      <PhotoGallery photos={selectedTicket.closingPhotos} title="Closing Photos" />
                    </div>
                  )}
                </div>

                <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 mb-4 border border-slate-200">
                  <p className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Messages</p>
                  {selectedTicket.messages && Array.isArray(selectedTicket.messages) && selectedTicket.messages.length > 0 ? (
                    <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                      {selectedTicket.messages.map((msg, idx) => (
                        <div key={idx} className="bg-white p-3 rounded-lg shadow-sm border border-slate-200">
                          <p className="text-sm text-slate-800 mb-2">{msg.text || msg.message}</p>
                          {msg.images && msg.images.length > 0 && (
                            <PhotoGallery photos={msg.images} title="" />
                          )}
                          <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                            <span>🕒</span>
                            {new Date(msg.timestamp || msg.createdAt).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">No messages yet</p>
                  )}
                </div>

                {selectedTicket.status !== "resolved" && (
                  <div className="space-y-4 bg-white border-t-2 border-slate-200 pt-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
                        Add Message
                      </label>
                      <textarea
                        className="w-full border-2 border-slate-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                        placeholder="Type your message here..."
                        rows="3"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
                        Attach Photos
                      </label>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        className="w-full text-sm text-slate-500 file:mr-4 file:py-3 file:px-6 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition cursor-pointer"
                        onChange={(e) => setMessagePhotos([...e.target.files])}
                      />
                      {messagePhotos.length > 0 && (
                        <div className="mt-2">
                          <PhotoGallery photos={messagePhotos} title={`${messagePhotos.length} photo(s) selected`} />
                        </div>
                      )}
                    </div>

                    <button
                      onClick={sendMessage}
                      className="w-full bg-gradient-to-r from-cyan-600 to-cyan-700 text-white py-3 rounded-lg font-bold hover:from-cyan-700 hover:to-cyan-800 transition-all shadow-md hover:shadow-lg transform hover:scale-[1.02]"
                    >
                      📤 Send Message
                    </button>

                    <div className="border-t-2 border-slate-200 pt-4">
                      <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
                        Close Ticket
                      </label>
                      <p className="text-xs text-slate-500 mb-3">
                        Upload photos showing the completed work to close this ticket
                      </p>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        className="w-full text-sm text-slate-500 file:mr-4 file:py-3 file:px-6 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100 transition cursor-pointer mb-3"
                        onChange={(e) => setClosePhotos([...e.target.files])}
                      />
                      {closePhotos.length > 0 && (
                        <div className="mb-3">
                          <PhotoGallery photos={closePhotos} title={`${closePhotos.length} closing photo(s) selected`} />
                        </div>
                      )}
                      <button
                        onClick={closeTicket}
                        disabled={closePhotos.length === 0}
                        className={`w-full py-3 rounded-lg font-bold transition-all shadow-md hover:shadow-lg transform hover:scale-[1.02] ${
                          closePhotos.length > 0
                            ? 'bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        ✅ Close Ticket
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-slate-200">
              <h2 className="font-bold text-2xl text-slate-800">✨ Create Cleaning Ticket</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-slate-400 hover:text-slate-600 text-3xl leading-none transition-colors"
              >
                ×
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full border-2 border-slate-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  value={type}
                  onChange={(e) => {
                    setType(e.target.value);
                    setStand("");
                    setHall("");
                  }}
                >
                  <option value="stand">Stand</option>
                  <option value="hall">Hall</option>
                </select>
              </div>

              {type === "stand" ? (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Stand <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full border-2 border-slate-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    value={stand}
                    onChange={(e) => setStand(e.target.value)}
                  >
                    <option value="">Select a stand</option>
                    {stands.map((s) => (
                      <option key={s._id} value={s.standNumber}>
                        {s.exhibitorName} — Stand {s.standNumber}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Hall <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full border-2 border-slate-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    value={hall}
                    onChange={(e) => setHall(e.target.value)}
                  >
                    <option value="">Select a hall</option>
                    {halls.map((h) => (
                      <option key={h._id} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Priority</label>
                <select
                  className="w-full border-2 border-slate-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  <option value="low">🟢 Low</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="high">🟠 High</option>
                  <option value="critical">🔴 Critical</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">SLA Time</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="1"
                    className="flex-1 border-2 border-slate-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    placeholder="24"
                    value={slaValue}
                    onChange={(e) => setSlaValue(e.target.value)}
                  />
                  <select
                    className="border-2 border-slate-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    value={slaUnit}
                    onChange={(e) => setSlaUnit(e.target.value)}
                  >
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                  </select>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  {slaUnit === "hours" 
                    ? `${slaValue} hour${slaValue > 1 ? 's' : ''} = ${slaValue * 60} minutes`
                    : `${slaValue} minute${slaValue > 1 ? 's' : ''}`
                  }
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="w-full border-2 border-slate-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  placeholder="Describe the cleaning issue in detail..."
                  rows="4"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Photos <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  className="w-full text-sm text-slate-500 file:mr-4 file:py-3 file:px-6 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition cursor-pointer"
                  onChange={(e) => setCreatePhotos([...e.target.files])}
                />
                {createPhotos.length > 0 && (
                  <div className="mt-3">
                    <PhotoGallery photos={createPhotos} title={`${createPhotos.length} photo(s) selected`} />
                  </div>
                )}
              </div>

              <button
                onClick={createTicket}
                disabled={!(type === "stand" ? stand : hall) || !description || createPhotos.length === 0}
                className={`w-full py-4 rounded-lg font-bold transition-all shadow-lg hover:shadow-xl transform hover:scale-[1.02] ${
                  (type === "stand" ? stand : hall) && description && createPhotos.length > 0
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {(type === "stand" ? stand : hall) && description && createPhotos.length > 0 ? '✅ Create Ticket' : '⚠️ Fill Required Fields'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clean;